import React from 'react'

import { IconType } from '@factorialco/f0-react'
import { ProcurementTypeItemFragment } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

interface UseProcurementTypeActionsProps {
  onEditProcurementType: (procurementType: ProcurementTypeItemFragment) => void
  onToggle: (procurementType: ProcurementTypeItemFragment) => void
  onDelete: (procurementType: ProcurementTypeItemFragment) => void
  onDuplicate: (procurementType: ProcurementTypeItemFragment) => void
}

interface ProcurementTypeAction {
  label: string
  icon?: IconType
  variant?: 'default' | 'secondary' | 'danger'
  type: 'primary' | 'secondary' | 'other'
  onClick: () => void
}

export const useProcurementTypeActions = ({
  onEditProcurementType,
  onToggle,
  onDelete,
  onDuplicate,
}: UseProcurementTypeActionsProps) => {
  const getActions = React.useCallback(
    (procurementType: ProcurementTypeItemFragment): ProcurementTypeAction[] => {
      const actions: ProcurementTypeAction[] = []

      // Add rest of actions when no identifier is present
      if (onEditProcurementType) {
        actions.push({
          label: i18n.t('procurement.purchase_requests.settings.types.actions_new.edit'),
          variant: 'secondary',
          type: 'other',
          onClick: () => onEditProcurementType(procurementType),
        })
      }

      // Toggle action - for software and custom types
      if (procurementType.enabled && onToggle) {
        actions.push({
          label: i18n.t('procurement.purchase_requests.settings.types.actions_new.disable'),
          type: 'other',
          onClick: () => onToggle(procurementType),
        })
      }

      if (onDuplicate) {
        actions.push({
          label: i18n.t('procurement.purchase_requests.settings.types.actions_new.duplicate'),
          type: 'other',
          onClick: () => onDuplicate(procurementType),
        })
      }

      // Delete action - only for custom types (no identifier)
      if (!procurementType.identifier && onDelete) {
        actions.push({
          label: i18n.t('procurement.purchase_requests.settings.types.actions_new.delete'),
          variant: 'danger',
          type: 'other',
          onClick: () => onDelete(procurementType),
        })
      }

      return actions
    },
    [onEditProcurementType, onToggle, onDelete, onDuplicate]
  )

  return { getActions }
}
